/**
 * Session side effects shared by the seller and owner areas (FE-05): language sync with the account,
 * post-login navigation and the deferred offline logout. Logout itself lives in `logout.ts`, so this
 * module (part of the auth screens' chunk, NFR-FE05-01) never imports the outbox or push code.
 */
import { NetworkError } from "@sos/api-client";
import { toast } from "/home/runner/work/sosdigital-pindrop/sosdigital-pindrop/packages/frontend/ui/src/toast/toast.ts";
import type { QueryClient } from "@tanstack/react-query";

import i18n, {
  type Language,
  currentLanguage,
  isExplicitLanguage,
  isLanguage,
  setLanguage,
} from "../../app/i18n";
import { api } from "../api/client";
import { broadcastAuth } from "./broadcast";
import { type Area, type Me, meQuery } from "./me";
import { areaHome, safeRedirect } from "./redirect";

export const LOGIN_STATE_KEY = "sos.login";
const PENDING_LOGOUT_KEY = "sos.pendingLogout";

/** Save the language on the account (`PATCH /v1/auth/me`). Offline: retried once when back online. */
export async function saveLocale(
  lang: Language,
  queryClient: QueryClient,
): Promise<"saved" | "deferred" | "failed"> {
  try {
    await api.PATCH("/v1/auth/me", { body: { locale: lang } });
    queryClient.setQueryData(meQuery.queryKey, (me) =>
      me ? { ...me, user: { ...me.user, locale: lang } } : me,
    );
    return "saved";
  } catch (error) {
    if (error instanceof NetworkError) {
      window.addEventListener("online", () => void saveLocale(lang, queryClient), { once: true });
      return "deferred";
    }
    return "failed";
  }
}

/**
 * FE-03 FR-FE03-15/19: an explicit device choice wins and is saved to the account; otherwise the
 * account's locale is applied without reload.
 */
export async function syncLocaleWithMe(me: Me, queryClient: QueryClient): Promise<void> {
  const current = currentLanguage();
  if (me.user.locale === current) return;
  if (isExplicitLanguage()) {
    await saveLocale(current, queryClient);
  } else if (isLanguage(me.user.locale)) {
    await setLanguage(me.user.locale);
  }
}

export interface LoginResult {
  role: string;
  redirect: string;
}

/**
 * FE-05 FR-FE05-10/11: refetch `me`, apply the language rule, tell other tabs, drop the previous
 * user's cached data, then go to the sanitized `redirect` (same area) or the server's redirect.
 */
export async function completeLogin(
  result: LoginResult,
  options: {
    audience: Area | null;
    redirect?: string;
    queryClient: QueryClient;
    navigate: (href: string) => void | Promise<void>;
  },
): Promise<void> {
  const { queryClient } = options;
  try {
    window.sessionStorage.removeItem(LOGIN_STATE_KEY);
  } catch {
    // ignore
  }
  queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
  const me = await queryClient.fetchQuery({ ...meQuery, staleTime: 0 });
  if (me) {
    await syncLocaleWithMe(me, queryClient);
    // FE-04 FR-FE04-31 (other users' queue and copies) and FR-FE05-12 (seller outbox flush),
    // loaded on demand so the outbox is not part of the auth chunk (NFR-FE05-01).
    const { afterLogin } = await import("../offline/postLogin");
    await afterLogin(me, queryClient);
  }
  broadcastAuth({ type: "login" });
  const area: Area = result.role === "owner" ? "owner" : "seller";
  if (options.audience && options.audience !== area) {
    await i18n.loadNamespaces("auth");
    const role = i18n.t(`auth:roles.${result.role === "owner" ? "owner" : "seller"}`);
    toast(i18n.t("auth:login.signedInAs", { role }));
  }
  const target = safeRedirect(options.redirect, area) ?? safeRedirect(result.redirect, area);
  await options.navigate(target ?? areaHome(area));
}

export function setPendingLogout(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(PENDING_LOGOUT_KEY, "true");
    else window.localStorage.removeItem(PENDING_LOGOUT_KEY);
  } catch {
    // ignore
  }
}

/** Send a logout that could not reach the server earlier (call at start, before `me`). */
export async function flushPendingLogout(): Promise<void> {
  let pending = false;
  try {
    pending = window.localStorage.getItem(PENDING_LOGOUT_KEY) === "true";
  } catch {
    return;
  }
  if (!pending) return;
  try {
    await api.POST("/v1/auth/logout");
    setPendingLogout(false);
  } catch (error) {
    if (!(error instanceof NetworkError)) setPendingLogout(false);
    else window.addEventListener("online", () => void flushPendingLogout(), { once: true });
  }
}
